import { useCallback, useEffect, useState } from "react";
import { type ActivityEntry, fetchActivityPage } from "../api/activity";
import { AppRequestError } from "../api/client";
import { DownloadIcon } from "../components/icons";
import { Screen } from "../components/Screen";
import { ErrorState, Loading } from "../components/states";
import { TakeACopy } from "../components/TakeACopy";
import { usePanel } from "../panelContext";

/**
 * **What's changed** — the audit trail as a screen (issue #304, `History.dc.html`).
 *
 * `ACTION#` has been exported since #235, so a family could already read their own record of
 * changes — inside a chat transcript, in numbered parts. This is the same record on a page, and
 * the one control on it is *Take a copy of everything*, which is the first real download the
 * product has ever offered (ADR 0020 decision 1's re-visit trigger).
 *
 * ## Three things it has to say that a list of rows does not
 *
 * - **How each change reached Boone.** `writtenVia` has been on the record since #291 and had
 *   nowhere to be read; the chip is it. Where the row names no channel this build can read, it
 *   says *not recorded* and makes no claim about when — the server's own words, because
 *   `WRITTEN_VIA` is deliberately a total map with no guessing in it.
 * - **That a redacted entry is redacted.** #114 rewrites an `ACTION#` in place rather than
 *   deleting it, precisely so *"you asked me to remember this, and later took it back"* stays
 *   legible. Hiding those rows would conceal that a deletion happened, which is the opposite of
 *   an audit trail's job; naming the fact would put back the residue the deletion removed.
 * - **Where the record thins, and why.** ADR 0003 decision 10 keeps a tool call 90 days and a
 *   membership change for ever, so scroll far enough and almost everything disappears. Left
 *   unexplained on the page a family opens when they are *already* suspicious, that reads as
 *   data loss. The line across the list says what it is, and the entries past it carry *kept
 *   for good*.
 *
 * ## Paging is fine here and was not on the memory screen
 *
 * That is not a reversal — #329's whole argument is that *"nothing is held back"* is a
 * completeness claim no single page can make. **This screen makes no such claim**: it is a log
 * you scroll, and the promise of *everything* lives in the file, where it is one read and one
 * document. So *Show more* costs nothing here, and the counts that would need a whole record
 * behind them are in `TakeACopy` rather than on this page.
 *
 * ## Read-only, and the only screen in the panel that is
 *
 * No proposals, no confirmation, no writes of any kind — reading a family's own record is not a
 * write. Nothing here needs `canAdminister` either: the audit trail is the family's, and every
 * tool that reads it already answers for any member of it.
 */
export function Activity({ familyId }: { familyId: string }) {
  const { api, families } = usePanel();
  const familyName = families.find((family) => family.familyId === familyId)?.name ?? null;

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [more, setMore] = useState(false);
  /** The last *Show more* did not come back. Cleared when another is attempted. */
  const [moreFailed, setMoreFailed] = useState(false);
  const [taking, setTaking] = useState(false);

  /*
   * **No `attempt` counter here, where every other screen has one.** That idiom exists so an
   * applied write can force a re-read (`Panel.tsx`, `Memory.tsx`, `Roster.tsx`); this screen
   * performs no writes, so there is nothing to re-read *after*, and a trigger nothing pulls is
   * a dependency somebody later has to work out the purpose of.
   */
  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    fetchActivityPage(api, familyId, null)
      .then((page) => {
        if (!live) return;
        setLoad({
          status: "ready",
          entries: page.records,
          cursor: page.nextCursor,
          keptSince: page.everythingKeptSince,
        });
      })
      .catch((error: unknown) => {
        if (live) setLoad({ status: "failed", detail: detailOf(error) });
      });
    return () => {
      live = false;
    };
  }, [api, familyId]);

  /**
   * Fetch the next page and append it.
   *
   * **The cursor is read out of the state updater rather than closed over**, so two quick
   * presses cannot both send the same one and append the same page twice. `more` disables the
   * control meanwhile, which handles the ordinary case; this handles the one where a render has
   * not caught up.
   */
  const showMore = useCallback(() => {
    if (load.status !== "ready" || load.cursor === null || more) return;
    const cursor = load.cursor;
    setMore(true);
    setMoreFailed(false);
    fetchActivityPage(api, familyId, cursor)
      .then((page) => {
        setLoad((current) =>
          current.status === "ready" && current.cursor === cursor
            ? {
                ...current,
                entries: [...current.entries, ...page.records],
                cursor: page.nextCursor,
              }
            : current,
        );
      })
      .catch(() => {
        /*
         * **Said, not swallowed** (PR #333 review). This was deliberately silent, on the
         * reasoning that the rows on screen are still true and a failure line would be the
         * loudest thing on a page about the past. Both halves are right and the conclusion was
         * not: a failed *Show more* and a successful one that returned nothing are the same
         * three pixels of nothing — the button re-enables and the list does not move. On the
         * one page a family opens *because* they suspect something is missing, that reads as
         * "there is nothing older", which is the strongest possible version of the wrong
         * answer.
         *
         * So the rows stay, the sentence is quiet rather than an `ErrorState`, and *Show more*
         * stays put as the retry.
         */
        setMoreFailed(true);
      })
      .finally(() => setMore(false));
  }, [api, familyId, load, more]);

  const action = (
    <button
      className="cta cta--quiet activity__take"
      type="button"
      onClick={() => setTaking(true)}
      disabled={load.status === "loading"}
    >
      <DownloadIcon />
      Take a copy of everything
    </button>
  );

  return (
    <Screen title={TITLE} blurb={BLURB} action={action}>
      {load.status === "loading" ? <Loading label="Reading what's changed…" /> : null}

      {load.status === "failed" ? (
        <ErrorState title="We couldn't read what's changed" body={load.detail ?? GENERIC_FAILURE} />
      ) : null}

      {load.status === "ready" ? (
        load.entries.length === 0 ? (
          <p className="activity__empty">
            Nothing has changed yet. Once you start telling Boone things, every change anyone makes
            shows up here.
          </p>
        ) : (
          <>
            {groupByDay(load.entries, load.keptSince).map((group) =>
              group.kind === "boundary" ? (
                <p className="activity__boundary" key="boundary">
                  <span>
                    Older than {group.days} days — from here only changes to who's in the family are
                    kept
                  </span>
                </p>
              ) : (
                <section className="activity__day" key={group.key}>
                  <h2 className="activity__day-head">{group.day}</h2>
                  <ul className="activity__list">
                    {group.entries.map((entry) => (
                      <Row entry={entry} key={entry.actionId} />
                    ))}
                  </ul>
                </section>
              ),
            )}

            {/* `aria-live` rather than `role="alert"`: this answers something the family just
                pressed, and it must not interrupt — it is a quiet note beside the retry, which
                is the control immediately below it. */}
            <p className="activity__missed" aria-live="polite">
              {moreFailed ? "We couldn't load older changes. Try again." : ""}
            </p>

            {load.cursor === null ? null : (
              <button className="cta cta--quiet" type="button" onClick={showMore} disabled={more}>
                {more ? "Reading…" : "Show more"}
              </button>
            )}
          </>
        )
      ) : null}

      {taking ? (
        <TakeACopy
          api={api}
          familyId={familyId}
          familyName={familyName}
          onClose={() => setTaking(false)}
        />
      ) : null}
    </Screen>
  );
}

/**
 * One change.
 *
 * **A redacted entry keeps its shape and loses its sentence**, which is `History.dc.html`'s
 * treatment and the honest one: the row is still there, dated, in its day — because a deletion
 * having happened is exactly what the audit trail is for — and what it used to say is gone.
 * The sentence is the server's `REDACTED_NOTE` said in the panel's voice, and it names nothing.
 *
 * **It says "keep" rather than "remember", and that is issue #309 rather than a rewording**
 * (PR #334 review). A redaction used to have one cause — a deleted durable fact — so the
 * sentence could name it. `delete_item` gives it a second, and `redacted` does not record
 * which: *"something you'd asked Boone to remember"* over a deleted shopping errand is a
 * confident falsehood on the page a family opens when they are already suspicious. The archive
 * retired the same sentence for the same reason, and this is the other surface that speaks it.
 * A discriminator saying which kind went would put back a hint about what the entry was about.
 */
function Row({ entry }: { entry: ActivityEntry }) {
  if (entry.redacted) {
    return (
      <li className="activity__row activity__row--redacted">
        <span className="activity__dot activity__dot--gone" aria-hidden="true" />
        <div className="activity__body">
          <p className="activity__what">
            Something you'd asked Boone to keep, and have since deleted.
          </p>
          <p className="activity__meta">
            <span>{timeOf(entry.at)}</span>
            {entry.redactedAt === null ? null : (
              <span className="activity__chip activity__chip--dashed">
                removed on {dayOf(entry.redactedAt)}
              </span>
            )}
          </p>
          <p className="activity__note">
            The record that it happened is kept; what it said is not.
          </p>
        </div>
      </li>
    );
  }

  return (
    <li className="activity__row">
      <span className="activity__dot" aria-hidden="true" />
      <div className="activity__body">
        <p className="activity__what">
          {/* `by` is null for somebody who has left — ADR 0019 decision 5 keeps their
              attribution on the record and `getRoster` is active-only, so this is an ordinary
              state. "Somebody who has left" beats a bare ULID, which is #117's complaint. */}
          {entry.by ?? "Somebody who has since left"} {entry.summary}
        </p>
        <p className="activity__meta">
          <span>{timeOf(entry.at)}</span>
          {/* The server's own words for the channel, never a mapping of ours: `WRITTEN_VIA` is
              a total map and its `unknown` says *not recorded* and makes no temporal claim. */}
          <span className="activity__chip">{entry.via}</span>
          {entry.askedVia === null ? null : (
            <span className="activity__chip">asked on {entry.askedVia}</span>
          )}
          {entry.keptForGood ? (
            <span className="activity__chip activity__chip--kept">kept for good</span>
          ) : null}
        </p>
      </div>
    </li>
  );
}

const TITLE = "What's changed";
const BLURB = "Every change anyone has made, and how it reached Boone.";
const GENERIC_FAILURE =
  "Something went wrong at our end. Try again in a moment — nothing you've told Boone is affected.";

type Load =
  | { status: "loading" }
  | { status: "failed"; detail: string | null }
  | {
      status: "ready";
      entries: readonly ActivityEntry[];
      cursor: string | null;
      /** The instant before which only permanent entries survive — the server's. */
      keptSince: string;
    };

type Group =
  /**
   * `key` is **not** {@link Group.day} (PR #333 review).
   *
   * `everythingKeptSince` is an instant, not midnight, so one calendar day can straddle it —
   * and then that day is emitted **twice**, either side of the boundary, with the same heading.
   * Keyed on the heading those are two siblings with one React key: the first paint often
   * survives, and the reconciliation after a *Show more* that inserts the boundary is where a
   * row visibly jumps to the wrong side of the line, on the screen whose entire job is saying
   * when something happened.
   *
   * So the key carries the group's position and the heading stays the day.
   */
  | { kind: "day"; key: string; day: string; entries: readonly ActivityEntry[] }
  | { kind: "boundary"; days: number };

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Days, newest first, with the retention boundary dropped in where it falls.
 *
 * **The boundary is emitted only once something is past it**, which is what stops it appearing
 * above an empty stretch on a family whose record is three weeks old — a line announcing that
 * older changes thin out, drawn where there are no older changes, is a worry manufactured out
 * of nothing. It follows that a family who has not pressed *Show more* far enough may not have
 * reached it yet, which is correct: it is a fact about the record, and it appears when the
 * record does.
 *
 * The number of days is **derived from the server's instant** rather than restated, so the
 * sentence and the position of the line cannot disagree even if the retention changes.
 */
function groupByDay(entries: readonly ActivityEntry[], keptSince: string): readonly Group[] {
  const boundary = Date.parse(keptSince);
  const groups: Group[] = [];
  let current: { day: string; entries: ActivityEntry[] } | null = null;
  let crossed = false;
  // The group's position in the list. It is what makes the key unique when one calendar day
  // appears on both sides of the boundary — see `Group`.
  const close = (group: { day: string; entries: ActivityEntry[] }): void => {
    groups.push({ kind: "day", key: `${groups.length}:${group.day}`, ...group });
  };

  for (const entry of entries) {
    const at = Date.parse(entry.at);
    if (!crossed && Number.isFinite(boundary) && Number.isFinite(at) && at < boundary) {
      crossed = true;
      if (current !== null) {
        close(current);
        current = null;
      }
      groups.push({ kind: "boundary", days: Math.round((Date.now() - boundary) / DAY_MS) });
    }
    const day = dayOf(entry.at);
    if (current === null || current.day !== day) {
      if (current !== null) close(current);
      current = { day, entries: [] };
    }
    current.entries.push(entry);
  }
  if (current !== null) close(current);
  return groups;
}

/**
 * A day heading — *Today*, *Yesterday*, *19 August*, *11 May 2025*.
 *
 * **The year appears once the date is not this one**, which the canvas's *11 May* does not show
 * and needs: this list runs back for ever on the far side of the retention boundary, so a bare
 * *11 May* on a permanent entry is ambiguous across every year the family has existed — on the
 * one screen whose job is to say when something happened.
 */
function dayOf(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const now = new Date();
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const daysAgo = Math.round((midnight(now) - midnight(at)) / DAY_MS);
  if (daysAgo === 0) return "Today";
  if (daysAgo === 1) return "Yesterday";
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** `2:14pm` — the canvas's format, in whatever the browser's locale calls it. */
function timeOf(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** The server's own sentence for a refusal, or `null` to fall back to a generic one. */
function detailOf(error: unknown): string | null {
  return error instanceof AppRequestError ? error.detail : null;
}
